import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { displayCategoriesForLang, codesForDisplayCategory } from '../../lib/factoryCategories';
// WHY a blocked request is blocked, in one place. Not WHETHER it is — that
// is the `offerable` prop, and the RPC's own request_is_offerable().
import { requestDistributionBlockReason } from '../../lib/requestStatus';
import {
  fetchDistributableFactories, fetchRequestInvites, createRequestInvites, sendRequestRfq,
  summarizeOutcomes, outcomeLabel, factoryDisplayName, factoryHasEmail,
  inviteStatusLabel, isInviteExpired, INVITE_STATUS, FAILED_OUTCOMES,
  DISTRIBUTE_SOFT_CAP, RFQ_BUTTON_BASE, rfqProductValue,
  fetchDistributionSuppliers, fetchFactoryCategories, routeRequestToSuppliers, unrouteRequest,
  isVerifiedSupplier, supplierDisplayName,
} from '../../lib/requestDistribution';

// The distribution block: pick factories for ONE request, create an invite each,
// then send them one at a time through InviteModal.
//
// ⚠️ CREATING AND SENDING ARE TWO ACTS, and the order is not negotiable.
// "Create invites" writes rows and returns links; it sends nothing. Sending goes
// through the approved factory_rfq template and can only reach a factory that
// ALREADY has an invite — the invite row owns the slug the template's button
// carries, so a mis-click on send cannot mint a link.
//
// Until 2026-09-18 this panel could not send at all, and the header said so: the
// ACCEPTED RISK in docs/TECH_DEBT.md is that claim_factory_by_slug is ungated,
// an invite slug is a bearer token for that factory's page, and the acceptance
// rested on distribution staying manual. What changed is not the appetite for
// that risk, it is the risk: register_factory_invite stopped binding ownership
// on 2026-09-17 (migration 20260917130000), so opening a link is now reading a
// request rather than acquiring a factory.
//
// The soft cap of ten and its warning still stand, and the dry run is still
// mandatory — see runSend for the reason, which is about honesty rather than
// caution.
//
// THE SELECTION MODEL IS COPIED FROM ConsoleClaimCampaign, NOT SHARED. That was
// a deliberate call (Majeed, 2026-09-17): that screen sends paid WhatsApp
// messages, and refactoring it to extract a picker is not a risk worth taking
// for this. The duplication is logged in docs/TECH_DEBT.md as something to
// unify later. If you are changing selection behaviour, check whether the other
// screen needs the same change — nothing enforces that they agree.

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');

// One greying, shared by every button this panel disables for the same reason,
// so a cancelled request looks the same wherever it is refused.
const DISABLED_STYLE = { opacity: 0.45, cursor: 'not-allowed' };

// One audience's table. Extracted only because there are now two of them and a
// copy-pasted second would drift — not a general-purpose component.
function FactoryGroup({ title, note, rows, isAr, td, th, selected, toggle, isSelectable, invitedIds, allSelected, onToggleAll }) {
  const selectable = rows.filter(isSelectable).length;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <strong style={{ fontSize: 12.5 }}>{title}</strong>
        <span style={{ fontSize: 12, color: 'var(--ac-faint)' }}>({rows.length})</span>
        <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{note}</span>
      </div>
      {rows.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--ac-faint)', margin: '4px 0 0' }}>
          {isAr ? 'لا أحد هنا ضمن هذه التصفية.' : 'Nobody here in this filter.'}
        </p>
      ) : (
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, width: 32 }}>
                <input type="checkbox" checked={allSelected} onChange={onToggleAll} disabled={selectable === 0}
                  title={isAr ? 'تحديد كل القابل للإضافة في هذه المجموعة' : 'Select every addable row in this group'} />
              </th>
              <th style={th}>{isAr ? 'المصنع' : 'Factory'}</th>
              <th style={th}>{isAr ? 'المدينة' : 'City'}</th>
              <th style={th}>{isAr ? 'ملاحظة' : 'Note'}</th>
            </tr></thead>
            <tbody>
              {rows.map((f) => {
                const already = invitedIds.has(f.id);
                const noEmail = !factoryHasEmail(f);
                return (
                  <tr key={f.id} style={{ opacity: already || noEmail ? 0.55 : 1 }}>
                    <td style={td}>
                      <input type="checkbox" checked={selected.has(f.id)}
                        onChange={() => toggle(f.id)} disabled={already || noEmail} />
                    </td>
                    <td style={td}>{factoryDisplayName(f)}</td>
                    <td style={td}>{nf(f.city)}</td>
                    <td style={td}>
                      {already
                        ? <span style={{ color: 'var(--ac-faint)' }}>{isAr ? 'لديها دعوة' : 'Already invited'}</span>
                        : noEmail
                          ? <span style={{ color: 'var(--ac-warn)' }}>{isAr ? 'لا بريد' : 'No email'}</span>
                          : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Self-registered suppliers (2026-10-12). Same table shape as FactoryGroup so
// the three groups read as one list; its own component because its rows are
// profiles, its action is routing, and its notes say different things.
function SupplierGroup({ rows, isAr, td, th, selected, toggle, isSelectable, routeBySup, allSelected, onToggleAll }) {
  const selectable = (rows || []).filter(isSelectable).length;
  const routeNote = (r) => {
    if (!r) return '';
    if (r.source === 'open_pool') return isAr ? 'وصله تلقائيًا' : 'Has it (open to all)';
    if (r.source === 'admin') return isAr ? 'أُرسل له' : 'Routed';
    if (r.source === 'public_link') return isAr ? 'فتح الرابط العام' : 'Opened the public link';
    return isAr ? 'وصله' : 'Has it';
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <strong style={{ fontSize: 12.5 }}>{isAr ? 'موردون سجّلوا بأنفسهم' : 'Self-registered suppliers'}</strong>
        <span style={{ fontSize: 12, color: 'var(--ac-faint)' }}>({(rows || []).length})</span>
        <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
          {isAr
            ? 'عندهم حساب: يظهر لهم الطلب في لوحتهم ويقدّمون عرضًا. عرض غير الموثّق يبقى مخفيًا عن التاجر حتى توثيقه.'
            : 'They have an account: the request shows in their dashboard. An unverified supplier’s quote stays hidden from the trader until approval.'}
        </span>
      </div>
      {rows == null ? (
        <div className="ac-skel" style={{ height: 48 }} />
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--ac-faint)', margin: '4px 0 0' }}>
          {isAr ? 'لا أحد هنا ضمن هذه التصفية.' : 'Nobody here in this filter.'}
        </p>
      ) : (
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, width: 32 }}>
                <input type="checkbox" checked={allSelected} onChange={onToggleAll} disabled={selectable === 0}
                  title={isAr ? 'تحديد كل من لم يصله الطلب في هذه المجموعة' : 'Select everyone in this group who does not have it yet'} />
              </th>
              <th style={th}>{isAr ? 'المورّد' : 'Supplier'}</th>
              <th style={th}>{isAr ? 'التوثيق' : 'Verified'}</th>
              <th style={th}>{isAr ? 'ملاحظة' : 'Note'}</th>
            </tr></thead>
            <tbody>
              {rows.map((x) => {
                const r = routeBySup.get(x.id);
                const verified = isVerifiedSupplier(x);
                return (
                  <tr key={x.id} style={{ opacity: r ? 0.55 : 1 }}>
                    <td style={td}>
                      <input type="checkbox" checked={selected.has(x.id)} onChange={() => toggle(x.id)} disabled={!!r} />
                    </td>
                    <td style={td}>{supplierDisplayName(x)}</td>
                    <td style={td}>
                      <span className={`ac-badge ${verified ? 'ok' : 'warn'}`}>
                        <span className="dot" />{verified ? (isAr ? 'موثّق' : 'Verified') : (isAr ? 'غير موثّق' : 'Unverified')}
                      </span>
                    </td>
                    <td style={td}><span style={{ color: 'var(--ac-faint)' }}>{routeNote(r)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// The send preview's message bubble: the VALUES that go into the approved
// template, laid out where the factory will read them.
//
// ⚠️ IT SHOWS PARAMETERS, NOT COPY, AND THAT IS THE DESIGN — do not "finish" it
// by pasting the approved English sentences in. The words live only in the
// Widers/Meta console; whatsapp-send/templates.ts:9 is explicit that nothing in
// the repo writes copy. A second copy here would agree with Meta on the day it
// was typed and disagree silently after the next edit there, and the preview
// would then be believed over the message actually sent. That is the en/en_US
// family in docs/TECH_DEBT.md, which already cost a template that could not be
// sent at all.
//
// What it DOES catch is every defect that is ours: {{2}} resolving to the
// Chinese title inside an English template, a factory name that came back
// blank, and above all a button carrying the claim slug instead of the invite
// slug — /f/<claim_slug> is a URL Meta accepts and delivers, so that failure
// shows up as a dead link on a live campaign and nowhere else.
function RfqBubble({ factoryName, product, slug, remaining, isAr }) {
  const slot = {
    display: 'flex', gap: 8, alignItems: 'baseline',
    padding: '5px 0', fontSize: 12.5, color: 'var(--ac-ink)',
  };
  const tag = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 11, color: 'var(--ac-faint)', flexShrink: 0, minWidth: 34,
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <p className="ac-flabel" style={{ margin: '0 0 6px' }}>
        {isAr ? 'أول مصنع سيستلمها' : 'First factory to receive it'}
      </p>

      {/* dir=ltr inside an RTL console on purpose: factory_rfq is approved in
          English only, so the message itself is left-to-right whatever language
          the admin is reading the console in. */}
      <div dir="ltr" style={{
        textAlign: 'left',
        background: 'var(--ac-ok-bg)',
        border: '1px solid var(--ac-ok-bd)',
        borderRadius: '10px 10px 10px 2px',
        padding: '10px 12px',
      }}>
        <div style={slot}>
          <span style={tag}>{'{{1}}'}</span>
          <strong style={{ fontWeight: 600 }}>{factoryName || '—'}</strong>
        </div>
        <div style={slot}>
          <span style={tag}>{'{{2}}'}</span>
          <span>{product || '—'}</span>
        </div>

        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--ac-ok-bd)' }}>
          {slug ? (
            <span style={{
              display: 'inline-block', fontSize: 12, color: 'var(--ac-ok)',
              fontWeight: 600, wordBreak: 'break-all',
            }}>
              {RFQ_BUTTON_BASE}{slug}
            </span>
          ) : (
            // Unreachable through the RPC — an `enqueued` row always has an
            // invite, because no_invite is checked before it. Rendered anyway
            // rather than showing a truncated link, since the panel's invite
            // list is client state and can be stale.
            <span style={{ fontSize: 12, color: 'var(--ac-warn)' }}>
              {isAr ? 'تعذّر عرض رابط الزر من هذه الشاشة' : 'Could not resolve the button link on this screen'}
            </span>
          )}
        </div>
      </div>

      <p style={{ fontSize: 11.5, color: 'var(--ac-faint)', lineHeight: 1.7, margin: '6px 0 0' }}>
        {isAr
          ? 'هذه القيم فقط هي ما يرسله النظام. نصّ الرسالة وعنوان الزر معتمدان في Meta ولا يُخزَّنان هنا.'
          : 'These values are all the system sends. The wording around them, and the button’s base URL, are approved at Meta and are not stored in this app.'}
        {remaining > 0 && (isAr
          ? ` و${remaining} مصنعًا آخر يستلمون الرسالة نفسها باسمهم ورابطهم.`
          : ` ${remaining} more ${remaining === 1 ? 'factory receives' : 'factories receive'} the same message with their own name and link.`)}
      </p>
    </div>
  );
}

/**
 * ⚠️ `offerable` IS A PROP, NOT SOMETHING THIS PANEL WORKS OUT.
 *
 * It used to call isRequestOfferable(request), and that shipped a production
 * incident on 2026-09-20: this panel is mounted from TWO screens that pass
 * differently-shaped rows. ConsoleRequestDetail passes the raw `requests` row;
 * ConsoleQuotations passes a BOARD row whose `status` has been overwritten with
 * a derived vocabulary (waiting / replied / closed / cancelled). Testing
 * 'replied' against ['open','offers_received'] greyed the distribute buttons on
 * healthy live requests — blocking real work, which is worse than the bug the
 * greying was added to prevent.
 *
 * Each screen now computes it in its own data layer, from the column it
 * actually has, and hands down a boolean. A component that is handed rows from
 * more than one source must not guess which one it got.
 */
export default function DistributePanel({ requestId, request, offerable = false, rawStatus, isAr, onOpenInvite, flash, initialCategory }) {
  const [invites, setInvites] = useState(null);
  const [facs, setFacs] = useState(null);
  const [q, setQ] = useState('');
  // Starts on the request's OWN category when the caller knows it. The trader
  // already said what this is; making the admin pick it again is asking a
  // question we have the answer to — and picking it wrong sends an electronics
  // RFQ to furniture factories. 'all' stays the fallback, and the admin can
  // widen it in one click.
  const [cat, setCat] = useState(initialCategory || 'all');
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // null | { phase: 'preview' | 'done', rows }
  const [dialog, setDialog] = useState(null);
  // Self-registered suppliers, this request's routing rows, and which supplier
  // ids are already a claimed factory (2026-10-12: one picker for everyone).
  const [supData, setSupData] = useState(null);
  const [selectedSup, setSelectedSup] = useState(() => new Set());
  // Category of every active factory, for the counts on the chips.
  const [facCats, setFacCats] = useState([]);

  const cats = useMemo(() => displayCategoriesForLang(isAr ? 'ar' : 'en'), [isAr]);

  const loadInvites = useCallback(() => {
    fetchRequestInvites(requestId).then(setInvites).catch((e) => {
      setErr(e.message || 'load failed'); setInvites([]);
    });
  }, [requestId]);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const loadSuppliers = useCallback(() => {
    fetchDistributionSuppliers(requestId).then(setSupData).catch((e) => {
      console.error('[DistributePanel] suppliers read failed:', e?.message || e);
      setErr(e.message || 'load failed');
      setSupData({ suppliers: [], routing: [], linkedSupplierIds: new Set() });
    });
  }, [requestId]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  useEffect(() => {
    fetchFactoryCategories().then(setFacCats).catch((e) => {
      console.error('[DistributePanel] category counts read failed:', e?.message || e);
    });
  }, []);

  // Re-query the server on every filter change rather than filtering a cached
  // list. The cap was 60 until 2026-10-12 and hid 48 of 108 factories from
  // «كل المصانع»; it is now DISTRIBUTE_FACTORY_LIMIT (1000) — see that constant.
  useEffect(() => {
    let cancelled = false;
    setFacs(null);
    const codes = cat === 'all' ? null : codesForDisplayCategory(cat);
    fetchDistributableFactories({ q, categoryCodes: codes })
      .then((rows) => { if (!cancelled) setFacs(rows); })
      .catch((e) => { if (!cancelled) { setErr(e.message || 'load failed'); setFacs([]); } });
    return () => { cancelled = true; };
  }, [q, cat]);

  // Factories that already have an invite are shown, but not selectable — the
  // RPC would return `existing` for them anyway, and letting them into the count
  // would make the confirmation dialog overstate what is about to happen.
  const invitedIds = useMemo(
    () => new Set((invites || []).map((i) => i.factory_id)),
    [invites],
  );

  const isSelectable = useCallback(
    (f) => !invitedIds.has(f.id) && factoryHasEmail(f),
    [invitedIds],
  );

  // ── Two audiences, not one list ───────────────────────────────────────────
  //
  // A claimed factory has an account: the invite asks it to quote, and it can,
  // immediately. An unclaimed one has no account at all: the same link lets it
  // READ the request, and pressing "reply" routes it through claiming its page
  // first. Same link, same RPC, two different things being asked of two
  // different people — so they are picked separately, counted separately, and
  // the heading says which is which. One mixed list made the admin work that
  // out per row from a note in the last column.
  const claimed = useMemo(() => (facs || []).filter((f) => f.linked_supplier_id), [facs]);
  const unclaimed = useMemo(() => (facs || []).filter((f) => !f.linked_supplier_id), [facs]);

  const selectableIn = useCallback((list) => list.filter(isSelectable), [isSelectable]);
  const allSelectedIn = useCallback((list) => {
    const s = selectableIn(list);
    return s.length > 0 && s.every((f) => selected.has(f.id));
  }, [selectableIn, selected]);
  const toggleAllIn = useCallback((list) => setSelected((prev) => {
    const next = new Set(prev);
    const s = list.filter(isSelectable);
    if (s.length > 0 && s.every((f) => next.has(f.id))) s.forEach((f) => next.delete(f.id));
    else s.forEach((f) => next.add(f.id));
    return next;
  }), [isSelectable]);

  // A selection survives a filter change on purpose — someone who picks under
  // one category and then looks at another has not changed their mind. The cost
  // is that the count can exceed what is on screen, so it has to be said out
  // loud rather than left for them to discover at the confirmation dialog.
  const offScreen = useMemo(() => {
    const visible = new Set((facs || []).map((f) => f.id));
    return [...selected].filter((id) => !visible.has(id)).length;
  }, [selected, facs]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Self-registered suppliers ────────────────────────────────────────────
  // Routing rows by supplier: who already has this request, and how. Positive
  // tests on `source` (golden rule 12) — each known source is named.
  const routeBySup = useMemo(() => {
    const m = new Map();
    for (const r of supData?.routing || []) m.set(r.supplier_id, r);
    return m;
  }, [supData]);
  const poolCount = useMemo(
    () => (supData?.routing || []).filter((r) => r.source === 'open_pool').length, [supData],
  );
  const handRouted = useMemo(
    () => (supData?.routing || []).filter((r) => r.source === 'admin' || r.source === 'public_link'),
    [supData],
  );
  const supById = useMemo(
    () => new Map((supData?.suppliers || []).map((x) => [x.id, x])), [supData],
  );

  // Filtered by the same chip and search as the factories. A supplier whose
  // speciality is free text (admin-seeded rows carry Arabic prose) or empty
  // matches no chip, so it shows under «الكل» only — never silently dropped.
  const supRows = useMemo(() => {
    if (!supData) return null;
    const codes = cat === 'all' ? null : codesForDisplayCategory(cat);
    const needle = q.trim().toLowerCase();
    return supData.suppliers
      .filter((x) => !supData.linkedSupplierIds.has(x.id))
      .filter((x) => !codes || codes.includes(String(x.speciality || '')))
      .filter((x) => !needle || [x.company_name, x.company_name_latin, x.full_name, x.city]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)))
      // Verified first: they are the ones whose quote reaches the trader.
      .sort((a, b) => (isVerifiedSupplier(b) - isVerifiedSupplier(a))
        || supplierDisplayName(a).localeCompare(supplierDisplayName(b)));
  }, [supData, cat, q]);

  const isSupSelectable = useCallback((x) => !routeBySup.has(x.id), [routeBySup]);
  const toggleSup = (id) => setSelectedSup((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllSup = () => setSelectedSup((prev) => {
    const next = new Set(prev);
    const s = (supRows || []).filter(isSupSelectable);
    if (s.length > 0 && s.every((x) => next.has(x.id))) s.forEach((x) => next.delete(x.id));
    else s.forEach((x) => next.add(x.id));
    return next;
  });

  // How many factories AND suppliers sit behind each chip, so an empty chip
  // can be hidden and a full one says how full. Suppliers are counted on
  // `speciality`, factories on `category` — the same code space.
  const chipCounts = useMemo(() => {
    const sups = (supData?.suppliers || []).filter((x) => !supData.linkedSupplierIds.has(x.id));
    const out = {};
    for (const c of cats) {
      const codes = c.key === 'all' ? null : codesForDisplayCategory(c.key);
      out[c.key] = codes
        ? facCats.filter((v) => codes.includes(v)).length + sups.filter((x) => codes.includes(String(x.speciality || ''))).length
        : facCats.length + sups.length;
    }
    return out;
  }, [cats, facCats, supData]);

  const routeSelected = async () => {
    if (!selectedSup.size) return;
    setErr(''); setBusy(true);
    try {
      const added = await routeRequestToSuppliers(requestId, [...selectedSup]);
      flash?.(isAr ? `وصل الطلب إلى ${added} مورّد.` : `Routed to ${added} supplier${added === 1 ? '' : 's'}.`);
      setSelectedSup(new Set());
      loadSuppliers();
    } catch (e) {
      console.error('[DistributePanel] route failed:', e?.code || '', e?.message || e);
      // REQUEST_NOT_OFFERABLE is raised by name so it can be said in the
      // admin's language (same handling RoutePanel had).
      setErr(String(e?.message || '').includes('REQUEST_NOT_OFFERABLE')
        ? (isAr ? 'هذا الطلب لم يعد يستقبل عروضاً — لا يمكن إرساله.' : 'This request is no longer collecting offers.')
        : (isAr ? 'تعذّر الإرسال للموردين.' : 'Could not route.'));
    }
    setBusy(false);
  };

  const unroute = async (supplierId) => {
    setErr(''); setBusy(true);
    try {
      const removed = await unrouteRequest(requestId, supplierId);
      flash?.(removed
        ? (isAr ? 'أُلغي الإرسال لهذا المورّد.' : 'Route removed.')
        : (isAr ? 'لم يُلغَ — وصله الطلب من رابط عام.' : 'Not removed — that route came from a public link.'));
      loadSuppliers();
    } catch (e) {
      console.error('[DistributePanel] unroute failed:', e?.code || '', e?.message || e);
      setErr(isAr ? 'تعذّر الإلغاء.' : 'Could not remove.');
    }
    setBusy(false);
  };

  // Preview ALWAYS precedes the write, and both come from the same RPC. The
  // dialog must never quote a number this client derived: the picker's view of a
  // factory can be minutes old, and the server recomputes eligibility.
  const run = async (phase) => {
    setErr(''); setBusy(true);
    try {
      const rows = await createRequestInvites(requestId, [...selected], { dryRun: phase === 'preview' });
      setDialog({ kind: 'invite', phase: phase === 'preview' ? 'preview' : 'done', rows });
      if (phase === 'commit') { setSelected(new Set()); loadInvites(); }
    } catch (e) {
      setErr(e.message || 'failed');
      setDialog(null);
    }
    setBusy(false);
  };

  // Send to every factory that already has an invite. The RPC is keyed on the
  // invite, so a factory already messaged for this request comes back
  // 'duplicate' rather than being messaged twice.
  //
  // The preview is not a confirmation step for its own sake. Consent and a
  // sendable number are checked in the RPC and reported per factory, because the
  // sender drops both AFTER queueing and without a word: of 112 factories, 91
  // have a number and 87 have consent. "Sent to 30" with a third of them dropped
  // is worse than a smaller number that is true.
  const runSend = async (phase) => {
    if (!invites || invites.length === 0) return;
    setErr(''); setBusy(true);
    try {
      const ids = [...new Set(invites.map((i) => i.factory_id).filter(Boolean))];
      const rows = await sendRequestRfq(requestId, ids, { dryRun: phase === 'preview' });
      setDialog({ kind: 'send', phase: phase === 'preview' ? 'preview' : 'done', rows });
      if (phase === 'commit') loadInvites();
    } catch (e) {
      setErr(e.message || 'failed');
      setDialog(null);
    }
    setBusy(false);
  };

  const copy = (text, msg) => {
    try { navigator.clipboard.writeText(text); flash?.(msg); } catch { /* noop */ }
  };

  const linkFor = (slug) => `${window.location.origin}/f/${slug}`;

  // ⚠️ `rawStatus` IS A PROP FOR EXACTLY THE REASON `offerable` IS.
  //
  // This panel is mounted from THREE screens that do not agree on what
  // `request.status` means. ConsoleRequestDetail and ConsoleManagedDetail pass
  // the raw requests row; ConsoleQuotations passes a BOARD row whose `status`
  // has been overwritten with a derived vocabulary (waiting / replied /
  // closed / cancelled). Reading request.status here would announce "an offer
  // was accepted" on a DELIVERED or a REJECTED request, because that board's
  // `closed` bucket holds five different statuses.
  //
  // So each screen hands down the column it actually has, the same contract
  // `offerable` has had since the 2026-09-20 incident. Absent, the wording
  // falls back to the generic sentence, which is true of every blocked
  // request and wrong about none.
  //
  // WORDING ONLY. `offerable` still decides whether anything may be sent, and
  // request_is_offerable() decides again inside the RPC.
  const notDistributable = requestDistributionBlockReason(rawStatus, isAr ? 'ar' : 'en');

  const th = { textAlign: isAr ? 'right' : 'left', fontSize: 11, color: 'var(--ac-faint)', padding: '6px 8px', fontWeight: 600 };
  const td = { fontSize: 12.5, color: 'var(--ac-ink)', padding: '7px 8px', borderTop: '1px solid var(--ac-line-2)' };

  return (
    <div className="ac-card" style={{ padding: 14, marginTop: 10, background: 'var(--ac-sunk)' }}>
      {err && <p style={{ color: 'var(--ac-danger)', fontSize: 12.5, margin: '0 0 8px' }}>{err}</p>}

      {/* ── STEP 1: who has this request ─────────────────────────────────────
          One question, answered for both audiences, before anything can be
          sent — so nobody is sent the same request twice (2026-10-12). */}
      <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>
        {isAr ? '١ — مَن وصله الطلب' : '1 — Who has this request'}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <span className="ac-badge neutral">
          {isAr ? `موردون: ${poolCount + handRouted.length}` : `Suppliers: ${poolCount + handRouted.length}`}
        </span>
        <span className="ac-badge neutral">
          {isAr ? `مصانع مدعوّة: ${(invites || []).length}` : `Factories invited: ${(invites || []).length}`}
        </span>
      </div>

      {poolCount > 0 && (
        <p style={{ fontSize: 12.5, lineHeight: 1.7, margin: '0 0 10px', padding: '8px 11px', borderRadius: 8, background: 'var(--ac-ok-bg)', border: '1px solid var(--ac-ok-bd)', color: 'var(--ac-ink)' }}>
          {isAr
            ? `طلب مباشر: وصل تلقائيًا لكل الموردين المسجّلين (${poolCount}) — لا حاجة لإرساله لهم. المصانع التي لم تستلم صفحتها لا يصلها إلا بدعوة من الأسفل.`
            : `Direct request: it already reached every registered supplier (${poolCount}) automatically. Factories without an account only get it through an invite below.`}
        </p>
      )}

      {handRouted.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <p className="ac-flabel" style={{ margin: '0 0 4px' }}>
            {isAr ? `موردون أُرسل لهم يدويًا (${handRouted.length})` : `Suppliers routed by hand (${handRouted.length})`}
          </p>
          {handRouted.map((r) => (
            <div key={r.supplier_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--ac-line-2)', fontSize: 12.5 }}>
              <span style={{ flex: 1 }}>{supById.has(r.supplier_id) ? supplierDisplayName(supById.get(r.supplier_id)) : r.supplier_id.slice(0, 8)}</span>
              <span style={{ color: 'var(--ac-faint)', fontSize: 11.5 }}>
                {r.source === 'admin' ? (isAr ? 'أرسلته الإدارة' : 'admin') : (isAr ? 'فتح الرابط العام' : 'public link')}
              </span>
              {r.source === 'admin' && (
                <button className="ac-btn ac-btn-sm" onClick={() => unroute(r.supplier_id)} disabled={busy}>
                  {isAr ? 'إلغاء' : 'Remove'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Existing invites ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <p className="ac-flabel" style={{ margin: 0 }}>
          {isAr ? 'المصانع المدعوّة' : 'Factories invited'}{invites ? ` (${invites.length})` : ''}
        </p>
        {invites && invites.length > 0 && (
          <button className="ac-btn ac-btn-sm ac-btn-primary" onClick={() => runSend('preview')}
            disabled={busy || !offerable}
            style={!offerable ? DISABLED_STYLE : undefined}
            title={!offerable ? notDistributable : (isAr
              ? 'يرسل قالب factory_rfq المعتمد لكل مصنع له دعوة هنا'
              : 'Sends the approved factory_rfq template to every factory with an invite here')}>
            {busy ? (isAr ? 'لحظة…' : 'Working…') : (isAr ? 'إرسال واتساب من معبر' : 'Send WhatsApp from MAABAR')}
          </button>
        )}
      </div>

      {/* ── Why the buttons are dead ──────────────────────────────────────────
          DISABLED, NOT HIDDEN, and the line is the point of it. A control that
          vanishes makes staff think the feature broke or that they are on the
          wrong screen; a greyed one that explains itself teaches the rule
          (owner, 2026-09-20).

          The RPCs remain the enforcement -- every push path checks
          request_is_offerable server-side and returns not_offerable or raises.
          This only stops the console ASKING a question whose answer is already
          no, so an admin never sees a dialog full of skips they could not have
          predicted. */}
      {!offerable && (
        <p style={{
          fontSize: 12, color: 'var(--ac-warn)', lineHeight: 1.7,
          background: 'var(--ac-warn-bg)', border: '1px solid var(--ac-warn-bd)',
          borderRadius: 8, padding: '8px 11px', margin: '0 0 12px',
        }}>
          {notDistributable}
        </p>
      )}

      {invites == null ? (
        <div className="ac-skel" style={{ height: 48 }} />
      ) : invites.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--ac-faint)', margin: '0 0 12px' }}>
          {isAr ? 'لم تُدعَ أي مصانع لهذا الطلب بعد.' : 'No factories invited to this request yet.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>{isAr ? 'المصنع' : 'Factory'}</th>
              <th style={th}>{isAr ? 'الحالة' : 'Status'}</th>
              <th style={th}>{isAr ? 'إجراءات' : 'Actions'}</th>
            </tr></thead>
            <tbody>
              {invites.map((i) => {
                const st = INVITE_STATUS[i.status];
                const expired = isInviteExpired(i);
                return (
                  <tr key={i.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{factoryDisplayName(i.factory) || '—'}</div>
                      <div dir="ltr" style={{ fontSize: 11, color: 'var(--ac-faint)' }}>{nf(i.factory_email)}</div>
                    </td>
                    <td style={td}>
                      <span className={`ac-badge ${st?.cls || 'neutral'}`}>
                        <span className="dot" />{inviteStatusLabel(i, isAr)}
                      </span>
                      {/* An expired invite still shows its status, because that
                          status is what happened. Expiry is a separate fact and
                          is what makes the link stop working, so it gets its own
                          badge rather than overwriting the history. */}
                      {expired && (
                        <span className="ac-badge danger" style={{ marginInlineStart: 6 }}>
                          {isAr ? 'منتهية' : 'Expired'}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="ac-btn ac-btn-sm"
                          onClick={() => copy(linkFor(i.slug), isAr ? 'نُسخ الرابط' : 'Link copied')}>
                          {isAr ? 'نسخ الرابط' : 'Copy link'}
                        </button>
                        {/* ⚠️ THERE IS ONE WHATSAPP PATH, and it is not here.
                            A per-invite wa.me button stood here until
                            2026-09-19. It opened the admin's own WhatsApp with
                            a pre-filled message, which sends something the
                            system never sees: no wa_messages row, no
                            event_key, so the duplicate guard cannot know it
                            happened and the send preview still counts that
                            factory as one that has heard nothing.

                            WhatsApp now leaves through "Send WhatsApp from
                            MAABAR" above, on the approved factory_rfq
                            template, logged per invite. Two buttons where one
                            is tracked and one is not made wa_messages a
                            partial record of what factories received, which is
                            worse than a smaller one that is true.

                            Email stays: it is a different channel, and it goes
                            through send-email, which does log. */}
                        <button className="ac-btn ac-btn-sm"
                          onClick={() => onOpenInvite?.(i.factory_id)}
                          disabled={!nf(i.factory_email)}
                          style={{ opacity: nf(i.factory_email) ? 1 : 0.5 }}>
                          {isAr ? 'إيميل' : 'Email'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* HIDDEN, not merely disabled, when the request cannot be distributed.
          Searching 112 factories and ticking boxes on a request nobody can be
          invited to is work that cannot produce anything: every button it
          leads to is already greyed, and admin_create_request_invites would
          refuse the call anyway.

          The whole picker goes, not only the search box and the chips. A list
          of factories with unusable checkboxes is the same dead control one
          step further down, and leaving it would invite the admin to select
          rows and then discover there is nothing to press.

          WHAT STAYS: the invites list above — who was already contacted is a
          fact the admin still needs, read-only — and the refusal sentence on
          the controls that remain, which now names the real reason. */}
      {offerable && (
      <>
      {/* ── Picker ───────────────────────────────────────────────────────── */}
      <p style={{ fontSize: 14, fontWeight: 700, margin: '18px 0 4px', paddingTop: 14, borderTop: '1px solid var(--ac-line-2)' }}>
        {isAr ? '٢ — أرسله لموردين ومصانع آخرين' : '2 — Send it to more suppliers and factories'}
      </p>
      <p style={{ fontSize: 12, color: 'var(--ac-faint)', lineHeight: 1.7, margin: '0 0 8px' }}>
        {isAr
          ? 'اختر الفئة أو ابحث بالاسم، حدّد من تريد، ثم اضغط الزر الذي يظهر. الموردون يصلهم الطلب في لوحتهم فورًا، والمصانع تُنشأ لها دعوة تُرسلها بعدها بالإيميل أو الواتساب من القسم ١.'
          : 'Pick a category or search, tick who you want, then press the button that appears. Suppliers get it in their dashboard at once; factories get an invite you then send by email or WhatsApp from section 1.'}
      </p>

      <input className="ac-input" style={{ maxWidth: 360, marginBottom: 8 }}
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={isAr ? 'ابحث بالاسم أو المدينة…' : 'Search name or city…'} />

      <div className="ac-chiprow" style={{ marginBottom: 10 }}>
        {/* Empty categories are hidden (a «طبية (0)» chip only ever led to
            "nobody here"); the selected one always stays so it can be left. */}
        {cats.filter((c) => c.key === 'all' || c.key === cat || chipCounts[c.key] > 0).map((c) => (
          <button key={c.key} className={`ac-chip${cat === c.key ? ' on' : ''}`} onClick={() => setCat(c.key)}>
            {/* Packaging factories live under this chip; staff looking for a
                paper-cup maker did not know to look there. Console-only hint —
                the shared label is buyer-facing and stays as it is. */}
            {c.key === 'restaurant_hospitality' ? (isAr ? `${c.label} والتغليف` : `${c.label} & Packaging`) : c.label}
            {chipCounts[c.key] != null ? ` (${chipCounts[c.key]})` : ''}
          </button>
        ))}
      </div>

      {selectedSup.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--ac-bg)' }}>
          <strong style={{ fontSize: 12.5 }}>
            {isAr ? `${selectedSup.size} مورّدًا محدّدًا` : `${selectedSup.size} supplier${selectedSup.size === 1 ? '' : 's'} selected`}
          </strong>
          <button className="ac-btn ac-btn-sm" onClick={() => setSelectedSup(new Set())} disabled={busy}>
            {isAr ? 'إلغاء التحديد' : 'Clear'}
          </button>
          <button className="ac-btn ac-btn-sm ac-btn-primary" onClick={routeSelected}
            disabled={busy || !offerable} style={!offerable ? DISABLED_STYLE : undefined}
            title={!offerable ? notDistributable : undefined}>
            {busy ? (isAr ? 'لحظة…' : 'Working…') : (isAr ? 'أرسل الطلب للموردين المحدّدين' : 'Send to selected suppliers')}
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="ac-bulkbar" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--ac-bg)' }}>
          <strong style={{ fontSize: 12.5 }}>
            {isAr ? `${selected.size} مصنعًا محدّدًا` : `${selected.size} factor${selected.size === 1 ? 'y' : 'ies'} selected`}
          </strong>
          {offScreen > 0 && (
            <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
              {isAr ? `منها ${offScreen} خارج التصفية الحالية` : `${offScreen} outside the current filter`}
            </span>
          )}
          {selected.size > DISTRIBUTE_SOFT_CAP && (
            <span style={{ fontSize: 11.5, color: 'var(--ac-warn)' }}>
              {isAr
                ? `أكثر من ${DISTRIBUTE_SOFT_CAP} — كل رابط يمنح ملكية المصنع لمن يفتحه أولاً.`
                : `More than ${DISTRIBUTE_SOFT_CAP} — each link grants factory ownership to whoever opens it first.`}
            </span>
          )}
          <button className="ac-btn ac-btn-sm" onClick={() => setSelected(new Set())} disabled={busy}>
            {isAr ? 'إلغاء التحديد' : 'Clear'}
          </button>
          <button className="ac-btn ac-btn-sm ac-btn-primary" onClick={() => run('preview')}
            disabled={busy || !offerable}
            style={!offerable ? DISABLED_STYLE : undefined}
            title={!offerable ? notDistributable : undefined}>
            {busy ? (isAr ? 'لحظة…' : 'Working…') : (isAr ? 'أنشئ دعوات للمصانع المحدّدة' : 'Create invites for selected factories')}
          </button>
        </div>
      )}

      <SupplierGroup
        rows={supRows} isAr={isAr} td={td} th={th}
        selected={selectedSup} toggle={toggleSup} isSelectable={isSupSelectable}
        routeBySup={routeBySup}
        allSelected={(supRows || []).filter(isSupSelectable).length > 0 && (supRows || []).filter(isSupSelectable).every((x) => selectedSup.has(x.id))}
        onToggleAll={toggleAllSup}
      />

      {facs == null ? (
        <div className="ac-skel" style={{ height: 70 }} />
      ) : facs.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--ac-faint)' }}>
          {isAr ? 'لا مصانع مطابقة.' : 'No matching factories.'}
        </p>
      ) : (
        <>
          <FactoryGroup
            title={isAr ? 'مصانع استلمت صفحتها — عندها حساب' : 'Factories that claimed their page — they have an account'}
            note={isAr
              ? 'يفتحون الطلب ويقدّمون عرضًا مباشرة.'
              : 'They open the request and quote straight away.'}
            rows={claimed} isAr={isAr} td={td} th={th}
            selected={selected} toggle={toggle} isSelectable={isSelectable}
            invitedIds={invitedIds}
            allSelected={allSelectedIn(claimed)} onToggleAll={() => toggleAllIn(claimed)}
          />
          <FactoryGroup
            title={isAr ? 'مصانع لم تستلم صفحتها بعد' : 'Factories that have not claimed their page'}
            note={isAr
              ? 'يقرأون الطلب أولاً، ولتقديم عرض يطالبون بصفحتهم في الطريق.'
              : 'They read the request first; replying routes them through claiming their page.'}
            rows={unclaimed} isAr={isAr} td={td} th={th}
            selected={selected} toggle={toggle} isSelectable={isSelectable}
            invitedIds={invitedIds}
            allSelected={allSelectedIn(unclaimed)} onToggleAll={() => toggleAllIn(unclaimed)}
          />
        </>
      )}
      </>
      )}


      {/* ── Preview / result dialog ──────────────────────────────────────── */}
      {dialog && (
        <div className="ac-modal-ov" onClick={() => setDialog(null)}>
          <div className="ac-modal" style={{ maxWidth: 520, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
            onClick={(e) => e.stopPropagation()}>
            {/* "تم" over a list where everything was skipped is a false
                report, and it is the most common outcome now that the RPCs
                refuse whole categories. The title is derived from the rows the
                SERVER returned -- the same source as the counts below, so the
                heading and the list cannot disagree.

                Three outcomes, because they mean different things to the
                person reading: everything worked, some of it worked, none of it
                did. Only the last two used to share a word. */}
            <p className="ac-modal-title">
              {dialog.phase === 'preview'
                ? (dialog.kind === 'send'
                  ? (isAr ? 'تأكيد الإرسال' : 'Confirm sending')
                  : (isAr ? 'تأكيد إنشاء الدعوات' : 'Confirm invite creation'))
                : (() => {
                  const rows = dialog.rows || [];
                  const failed = rows.filter((x) => FAILED_OUTCOMES.includes(x.outcome)).length;
                  const ok = rows.length - failed;
                  if (ok === 0) return isAr ? 'لم يُنفَّذ شيء' : 'Nothing was done';
                  if (failed > 0) return isAr ? 'تم جزئياً' : 'Partly done';
                  return isAr ? 'تم' : 'Done';
                })()}
            </p>

            {/* The bubble previews ONE factory: the first that will actually be
                sent to, not the first row. A bubble for a factory about to be
                skipped as no_consent would preview a message nobody receives.
                Only on the send preview — there is no message to show for
                invite creation, and none to show once it is done. */}
            {dialog.kind === 'send' && dialog.phase === 'preview' && (() => {
              const going = (dialog.rows || []).filter((r) => r.outcome === 'enqueued');
              if (going.length === 0) return null;
              const first = going[0];
              return (
                <RfqBubble
                  // {{1}} comes back from the RPC, so it needs no mirror here:
                  // it is the value the send itself will use, not a second
                  // guess at it. {{2}} is not returned and has to be resolved
                  // client-side — see rfqProductValue for the rule that keeps
                  // the two expressions in step.
                  factoryName={first.factory_name}
                  product={rfqProductValue(request)}
                  slug={(invites || []).find((i) => i.factory_id === first.factory_id)?.slug || ''}
                  remaining={going.length - 1}
                  isAr={isAr}
                />
              );
            })()}

            {/* Counts come from the ROWS THE SERVER RETURNED, never re-derived
                here — that is the whole reason the RPC has a dry run. */}
            <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0', fontSize: 13 }}>
              {Object.entries(summarizeOutcomes(dialog.rows)).map(([k, n]) => (
                <li key={k} style={{ padding: '3px 0', color: FAILED_OUTCOMES.includes(k) ? 'var(--ac-warn)' : 'var(--ac-ink)' }}>
                  {n} — {outcomeLabel(k, isAr, dialog.phase === 'done')}
                </li>
              ))}
            </ul>

            {dialog.phase === 'preview' && (
              <p style={{ fontSize: 12, color: 'var(--ac-faint)', lineHeight: 1.7, margin: '0 0 12px' }}>
                {dialog.kind === 'send'
                  ? (isAr
                    ? 'رسالة واتساب حقيقية بقالب معتمد، تُحتسب عليك. المتخطّى أعلاه لن يصله شيء ولن يُعاد المحاولة له تلقائيًا.'
                    : 'A real WhatsApp message on an approved template, and it is billed. Anything skipped above receives nothing, and nothing retries it for you.')
                  : (isAr
                    ? 'كل دعوة تُنشئ رابطًا يفتح الطلب للمصنع. أنشئ فقط ما ستُرسله الآن.'
                    : 'Each invite mints a link that opens the request for that factory. Create only what you will send now.')}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ac-btn" onClick={() => setDialog(null)}>
                {dialog.phase === 'preview' ? (isAr ? 'إلغاء' : 'Cancel') : (isAr ? 'إغلاق' : 'Close')}
              </button>
              {dialog.phase === 'preview' && (
                <button className="ac-btn ac-btn-primary" disabled={busy}
                  onClick={() => (dialog.kind === 'send' ? runSend('commit') : run('commit'))}>
                  {busy
                    ? (isAr ? 'لحظة…' : 'Working…')
                    : dialog.kind === 'send'
                      ? (isAr ? 'إرسال' : 'Send')
                      : (isAr ? 'إنشاء' : 'Create')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
